import { Button } from '../../components/ui/Button';
import { ColorField } from '../../components/ui/ColorField';

/** What a colour added to the list starts as. */
const NEW_COLOR = '#ffffff';
const SMALL_BUTTON = 'px-2 py-1 text-[12px]';

type Props = {
  'aria-label': string;
  value: readonly string[];
  min: number;
  max: number;
  disabled: boolean;
  onChange: (value: string[]) => void;
};

/** A list of colour pickers the operator can add to and remove from, kept between `min` and `max`. */
export function ColorListField({ value, min, max, disabled, onChange, ...rest }: Props) {
  const label = rest['aria-label'];
  const canRemove = !disabled && value.length > min;
  const canAdd = !disabled && value.length < max;

  const replace = (index: number, color: string) =>
    onChange(value.map((current, i) => (i === index ? color : current)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1.5">
      {value.map((color, index) => (
        // Rows hold no state of their own and are never reordered, so the
        // position is a stable key; colours may repeat, so they are not
        <div key={index} className="flex items-center justify-between gap-2">
          <ColorField
            aria-label={`${label} ${index + 1}`}
            value={color}
            disabled={disabled}
            onChange={(next) => replace(index, next)}
          />
          <Button
            variant="ghost"
            className={SMALL_BUTTON}
            aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
            disabled={!canRemove}
            onClick={() => remove(index)}
          >
            −
          </Button>
        </div>
      ))}
      <Button
        variant="ghost"
        className={`self-start ${SMALL_BUTTON}`}
        disabled={!canAdd}
        onClick={() => onChange([...value, NEW_COLOR])}
      >
        + Add colour
      </Button>
    </div>
  );
}
